// `m8t platform enable-auto-update` — retrofit the updater (MI + cron CA-Job +
// role assignments + `system/infra-params` backfill) onto an install that
// predates auto-update. The bicep does the heavy lifting: main.bicep
// provisions the updater MI + Container Apps Job + ALL role assignments
// (Owner@RG + Foundry User@account + Storage Table + Storage Blob + AcrPull)
// whenever `provisionUpdater=true` AND `installerImage` is non-empty (the
// `provisionUpdaterEffective` gate) — this command does NOT imperatively
// create the MI or grant roles.
//
// This command's job is narrower: recover the install's suffix + params,
// re-run the SAME bicep with the updater switched on, and backfill the
// `system/infra-params` row so future converges (and the updater job itself)
// never have to re-derive the suffix. Idempotent — safely re-runnable, and
// safe to run on a fresh install too (the bicep no-ops the parts that already
// match live state).
import { Command, Option } from "clipanion";
import { confirm } from "@inquirer/prompts";
import { DefaultAzureCredential } from "@azure/identity";
import { M8tCommand } from "../../lib/m8t-command.js";
import { resolveGatewayContext } from "../../lib/gateway-context.js";
import { getAzAccount } from "../../lib/auth.js";
import { runAz } from "../../lib/az.js";
import { parseContainerAppResourceId } from "../../lib/platform-update.js";
import { resolveFoundryProject } from "../../lib/foundry-project.js";
import { readLiveVoiceInternalSecret } from "../../lib/gateway-live-params.js";
import {
  runBicepDeployment,
  resolveRepoRoot,
  resolveFoundryResourceId,
  resolveAcrResourceId,
  resolveAcrPullIdentity,
  buildBicepParams,
  parseFoundryTracingMode,
  type BicepParams,
} from "../../lib/deploy.js";
import { openInfraParamsTable, readInfraParams, deriveSuffixVerified, writeInfraParams } from "../../lib/platform-infra-params.js";
import { LocalCliError } from "../../lib/errors.js";
import { colors, renderJson, renderKeyValueBlock, resolveOutputMode } from "../../lib/output.js";

export class PlatformEnableAutoUpdateCommand extends M8tCommand {
  static paths = [["platform", "enable-auto-update"]];
  static usage = Command.Usage({
    category: "Platform",
    description: "Retrofit the auto-updater (MI + cron job + role assignments) onto an existing install.",
    details:
      "For an install that predates the Platform Update Framework: recovers the deployment's resource-name suffix (from the stamped system/infra-params row, or by verified derivation from the live gateway), recovers the other bicep params from live state (WITHOUT changing the deployed gateway image), and re-runs deploy/main.bicep with provisionUpdater=true — which provisions the updater managed identity, the cron Container Apps Job, and every role assignment (Owner@RG + Foundry User@account + Storage Table + Storage Blob + AcrPull), all scoped to the resource group. On success, backfills system/infra-params so future converges never have to re-derive the suffix. Idempotent — safe to re-run.",
  });

  subscription = Option.String("--subscription");
  resourceGroup = Option.String("--resource-group", {
    description: "m8t resource group to disambiguate the gateway (multi-deployment subscriptions).",
  });
  suffix = Option.String("--suffix", {
    description: "The existing deployment's resource-name suffix. Required when it cannot be recovered from system/infra-params or the live gateway.",
  });
  installerImage = Option.String("--installer-image", {
    required: true,
    description: "Updater CA-Job image ref (the converge engine at the current release). Required — an empty image would skip provisioning.",
  });
  updateCron = Option.String("--update-cron", {
    description: "Cron schedule for the updater job (bicep default applies when omitted).",
  });
  channelUrl = Option.String("--channel-url", {
    description: "Release-channel URL the updater job polls (bicep default applies when omitted).",
  });
  location = Option.String("--location", {
    description: "Region for the updater identity + job. Defaults to this install's stamped region, then to the resource group's existing resources.",
  });
  foundryTracing = Option.String("--foundry-tracing", {
    description: "project | account | skip. Omitting it keeps the value already stamped on this install; only an install with nothing stamped falls through to the default (project).",
  });
  endpoint = Option.String("--endpoint", {
    description: "Foundry project endpoint URL. Disambiguates the project in a multi-project subscription.",
  });
  yes = Option.Boolean("--yes", false);
  output = Option.String("--output");

  async executeCommand(): Promise<number> {
    const mode = resolveOutputMode(
      this.output as "pretty" | "json" | "auto" | undefined,
      this.context.stdout as NodeJS.WriteStream,
    );
    const interactive = (this.context.stdout as NodeJS.WriteStream).isTTY === true;
    const log = (msg: string) => {
      if (mode !== "json") this.context.stdout.write(msg + "\n");
    };
    const onProgress = (m: string) => {
      log(colors.dim(m));
    };

    const gw = await resolveGatewayContext({
      interactive: mode !== "json",
      subscriptionId: this.subscription,
      resourceGroup: this.resourceGroup,
    });
    const { resourceGroup, name: gatewayName } = parseContainerAppResourceId(gw.containerAppResourceId);
    const credential = new DefaultAzureCredential();
    const account = await getAzAccount();
    const subscriptionId = this.subscription ?? account.subscriptionId;

    // 1. Recover the suffix: --suffix wins, then the stamped system/infra-params
    //    row, then a VERIFIED derivation from the live gateway. Refuse — never
    //    guess — when none of the three resolve (mirrors platform converge.ts's
    //    recoverSuffix, which is the headless updater job's own recovery path).
    const explicitSuffix = typeof this.suffix === "string" && this.suffix.length > 0 ? this.suffix : undefined;
    onProgress("recovering the deployment's resource-name suffix…");
    const infraTable = await openInfraParamsTable({ credential, subscriptionId, resourceGroup });
    const stampedParams = await readInfraParams(infraTable);
    const derivedSuffix = explicitSuffix
      ? undefined
      : (stampedParams?.suffix ?? (await deriveSuffixVerified({ credential, subscriptionId, resourceGroup })) ?? undefined);
    const suffix = explicitSuffix ?? derivedSuffix;
    if (!suffix) {
      throw new LocalCliError({
        code: "PLATFORM_ENABLE_AUTO_UPDATE_SUFFIX_UNRECOVERABLE",
        message:
          "Could not recover the deployment's resource-name suffix: no --suffix was given, no system/infra-params row is stamped, and deriving it from the live gateway did not verify (0 or >1 gateway, or no matching CAE).",
        hint: "Pass --suffix <existing-suffix> explicitly (from the deployed resource names, e.g. m8t-gateway-<suffix>).",
      });
    }
    // A DERIVED (not explicit, not previously-stamped) suffix is a best-effort
    // guess verified by live-state cross-check — confirm it interactively before
    // provisioning against it, unless the caller opted out with --yes.
    if (!explicitSuffix && !stampedParams && !this.yes && interactive) {
      const ok = await confirm({
        message: `Derived suffix '${suffix}' from the live gateway — proceed?`,
        default: true,
      });
      if (!ok) {
        log(colors.dim("aborted — no change. Re-run with --suffix <suffix> to specify it explicitly."));
        return 0;
      }
    }

    onProgress("resolving Foundry project…");
    const project = await resolveFoundryProject({
      credential,
      subscriptionId,
      interactive,
      endpoint: typeof this.endpoint === "string" ? this.endpoint : undefined,
    });

    // 2. Recover the bicep params from live state. Deliberately does NOT run
    //    resolveBicepParamsForConverge (platform-converge.ts) — that resolver
    //    derives imageRef from a target release manifest, which would roll the
    //    gateway image as a side effect. A retrofit must leave the deployed
    //    gateway image untouched, so imageRef comes from `az containerapp show`
    //    on the live gateway instead.
    onProgress("reading the live gateway image (left unchanged)…");
    const imageRef = (
      await runAz(["containerapp", "show", "-n", gatewayName, "-g", resourceGroup, "--query", "properties.template.containers[0].image", "-o", "tsv"])
    ).trim();
    if (!imageRef) {
      throw new LocalCliError({
        code: "PLATFORM_ENABLE_AUTO_UPDATE_NO_IMAGE",
        message: `Could not read the live image for gateway '${gatewayName}' in resource group ${resourceGroup}.`,
      });
    }

    // main.bicep defaults voiceInternalSecret to newGuid(), which re-mints on every
    // deployment and rolls BOTH the gateway and the voice relay. Read the live value
    // and pass it back so this re-run is a genuine no-op. Never logged, never stored.
    const voiceInternalSecret = await readLiveVoiceInternalSecret({
      gatewayName,
      resourceGroup,
      subscriptionId,
    });

    // An explicit --location wins, then the value this install was actually
    // stamped with. `az resource list … [0].location` is the last resort: it
    // picks an ARBITRARY resource, which can disagree with the install's region.
    // The stamp is the install's own recorded answer, so preferring a guess over
    // it made the row's whole purpose conditional on remembering a flag.
    // A stamped EMPTY string must fall through to the guess, not short-circuit
    // into the no-location refusal, whose message blames an empty resource group
    // rather than the row it actually came from.
    const stampedLocation = stampedParams?.bicep.location;
    const location =
      typeof this.location === "string" && this.location.length > 0
        ? this.location
        : (stampedLocation !== undefined && stampedLocation.length > 0
          ? stampedLocation
          : (await runAz(["resource", "list", "-g", resourceGroup, "--subscription", subscriptionId, "--query", "[0].location", "-o", "tsv"])).trim());
    if (!location) {
      throw new LocalCliError({
        code: "PLATFORM_ENABLE_AUTO_UPDATE_NO_LOCATION",
        message: `Could not resolve a location for resource group ${resourceGroup} — it may be empty.`,
      });
    }

    const foundryResourceId = await resolveFoundryResourceId(project.endpoint);
    // This command has no --acrpull-identity, so resolveAcrPullIdentity({}) is
    // always ''. Without the stamped fallback, a BYO-identity install (private
    // ACR) had its identity CLEARED from both the deployment and the row by one
    // run of this command, after which the bicep provisions its own instead —
    // the same silent change-on-a-live-install as the two fields below.
    //
    // Note `??` is NOT the operator here: resolveAcrPullIdentity returns an
    // empty STRING when unset, and empty is precisely the case that must fall
    // through to the stamp. Written as an explicit check so neither a reader nor
    // a lint autofix can turn it into the wrong thing.
    const resolvedAcrPullIdentity = resolveAcrPullIdentity({});
    const acrPullIdentityResourceId =
      resolvedAcrPullIdentity !== ""
        ? resolvedAcrPullIdentity
        : (stampedParams?.bicep.acrPullIdentityResourceId ?? "");
    // Resolved unconditionally, NOT zeroed when an identity is present. The two
    // bicep gates read it differently: `provisionAcrPull` needs an EMPTY identity
    // (main.bicep), so both-set cannot double-provision, while the updater MI's
    // AcrPull grant keys only off this value (modules/updater-roles.bicep).
    // Zeroing it therefore silently withdrew that grant from exactly the
    // BYO-identity installs the inheritance above exists to protect.
    const acrResourceId = await resolveAcrResourceId({ imageRef });

    const recoveredParams: BicepParams = {
      location,
      suffix,
      imageRef,
      tenantId: gw.gatewayTenantId,
      clientId: gw.gatewayClientId,
      foundryResourceId,
      foundryProjectEndpoint: project.endpoint,
      acrPullIdentityResourceId,
      acrResourceId,
      // This row is REPLACED wholesale, so an absent flag must INHERIT the stamped
      // value rather than drop it. Both optional fields need this, for the same
      // reason and with the same consequence:
      //
      //   foundryTracingMode — dropping it re-applied main.bicep's `project`
      //     default, so an install that deliberately opted out of tracing had it
      //     switched back on by an unrelated re-run of this command. That is
      //     exactly the silent posture change we refuse to make for a customer.
      //   channelUrl — dropping it re-applied bicep's empty default, so the
      //     gateway and updater job lost their release channel and the install
      //     silently stopped polling for updates. The field exists in InfraParams
      //     precisely so a converge re-emits it; dropping it defeats its purpose.
      //
      // The stamped value is re-validated rather than cast: the row is typed
      // `string` and JSON-parsed unchecked, so a bad value would otherwise reach
      // ARM and come back as an opaque InvalidTemplate instead of a local refusal.
      //
      // An unrecognised stamped mode REFUSES rather than dropping to the bicep
      // default. Dropping would resolve to `project` and switch tracing ON for an
      // install whose stored configuration said something else — the silent
      // posture change this whole block exists to prevent. It also covers the
      // forward case: a newer CLI stamping a mode this build does not know should
      // not be clobbered by this build. An explicit flag rescues it either way,
      // which is why the flag is parsed FIRST and the stamp only read if absent.
      ...(() => {
        const explicit = parseFoundryTracingMode(this.foundryTracing);
        if (explicit) return { foundryTracingMode: explicit };
        const raw = stampedParams?.bicep.foundryTracingMode;
        if (raw === undefined) return {};
        try {
          // Not `mode ? … : {}`: past the undefined guard the parser either
          // throws or returns a mode, so a falsy branch here would be dead code
          // that silently restores the drop-on-absent bug if the guard moves.
          return { foundryTracingMode: parseFoundryTracingMode(raw) };
        } catch {
          throw new LocalCliError({
            code: "PLATFORM_ENABLE_AUTO_UPDATE_UNKNOWN_STAMPED_TRACING",
            message: `This install's stored configuration records a Foundry tracing mode this version does not recognise: '${raw}'.`,
            hint: "Pass --foundry-tracing project|account|skip to replace it. It is not dropped automatically, because that would re-enable tracing on an install whose configuration says otherwise.",
          });
        }
      })(),
      ...(() => {
        const url = (typeof this.channelUrl === "string" && this.channelUrl.length > 0)
          ? this.channelUrl
          : stampedParams?.bicep.channelUrl;
        return url ? { channelUrl: url } : {};
      })(),
    };

    // 3. Run the SAME main.bicep converge with the updater switched on. The
    //    bicep's own provisionUpdaterEffective gate (provisionUpdater=true AND
    //    installerImage non-empty) is what actually provisions the updater MI,
    //    the cron job, and every role assignment — all RG-scoped.
    onProgress("running Bicep deployment to provision the updater (~2-4 min)…");
    const repoRoot = await resolveRepoRoot();
    await runBicepDeployment({
      resourceGroup,
      repoRoot,
      params: buildBicepParams({
        ...recoveredParams,
        provisionUpdater: true,
        installerImage: this.installerImage,
        ...(voiceInternalSecret ? { voiceInternalSecret } : {}),
        ...(typeof this.updateCron === "string" ? { updateCron: this.updateCron } : {}),
      }),
      deploymentName: `m8t-enable-auto-update-${suffix.slice(0, 12)}`,
    });

    // 4. Backfill system/infra-params from the now-verified suffix + params so
    //    future converges (and the updater job itself) never re-derive it.
    onProgress("backfilling system/infra-params…");
    await writeInfraParams(
      {
        credential,
        subscriptionId,
        resourceGroup,
        retryHint: "The role assignment persists — retry 'm8t platform enable-auto-update' in a few minutes.",
        onProgress,
      },
      {
        schemaVersion: 1,
        suffix,
        bicep: recoveredParams,
        updatedAt: new Date().toISOString(),
      },
    );

    if (mode === "json") {
      this.context.stdout.write(renderJson({ suffix, resourceGroup, installerImage: this.installerImage, enabled: true }) + "\n");
      return 0;
    }
    this.context.stdout.write(
      renderKeyValueBlock([
        { key: "suffix", value: suffix },
        { key: "resource group", value: resourceGroup },
        { key: "installer image", value: this.installerImage },
        { key: "infra-params", value: "backfilled" },
      ]) + "\n",
    );
    log(colors.success("auto-update enabled — the updater job will start converging on its next cron tick ✓"));
    log(colors.dim("next: 'm8t platform status' to check drift, or 'm8t platform update' to converge interactively at any time."));
    return 0;
  }
}
